import { type PermissionedResource } from '@lib/permission';
import { type Inspection } from '@lib/spproject/types';
import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type IFolder } from './folderEndpoints';

export interface IProject extends PermissionedResource {
  projectInfo: ProjectInfo;
  inspections: Inspection.Info[];
  parentFolder?: PartialProjectFolderInfo | string | null;
  /** Stored file size in bytes. */
  size?: number;
}

export interface ProjectInfo {
  version: number;
  id: string;
  name: string;
  isProtected?: boolean;
  dateCreated: string;
  dateModified: string;
}

export type PartialProjectFolderInfo = Pick<IFolder, '_id' | 'name' | 'path'> | null;

/**
 * The parent folder is populated on single-project and search responses but arrives as a
 * bare id elsewhere, and a project can sit outside any folder — so callers that want a
 * path for the URL ask here rather than reaching into the field.
 */
export const folderPathOf = (project?: IProject): string =>
  project?.parentFolder && typeof project.parentFolder !== 'string' ? project.parentFolder.path : '';

export const projectApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<IProject[], { search?: string; }>({
      query: ({ search }) => ({
        url: '/v1/projects',
        params: search ? { search } : undefined,
      }),
      keepUnusedDataFor: 60,
      providesTags: ['Project'],
      onQueryStarted: handleApiResult(),
    }),
    getProject: builder.query<IProject, { id: string, showError?: boolean }>({
      query: ({ id }) => `/v1/projects/${id}`,
      providesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
      onQueryStarted: async (arg, api) => {
        if (arg.showError ?? true) return handleApiResult()(arg, api);
      },
    }),
    getProjectFile: builder.query<string, string>({
      query: (id) => ({
        url: `/v1/projects/${id}/file`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
      onQueryStarted: handleApiResult(),
    }),
    getProjectThumbnail: builder.query<string, string>({
      query: (id) => ({
        url: `/v1/projects/${id}/thumbnail`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
    }),
    addProject: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: '/v1/projects',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: handleApiResult('Api_Project_Add_Successful'),
    }),
    updateProject: builder.mutation<void, { id: string, formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/v1/projects/${id}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: async (arg, api) => {
        api.queryFulfilled.then(() => {
          const cached = thumbnailCache.get(arg.id);
          if (cached) URL.revokeObjectURL(cached);
          thumbnailCache.delete(arg.id);
        });
        return handleApiResult('Api_Project_Update_Successful')(arg, api);
      },
    }),
    patchProject: builder.mutation<
      void,
      { id: string, showSuccess?: boolean } & Omit<IProject, 'projectInfo' | 'inspections'>
    >({
      query: ({ id, showSuccess: _showSuccess, ...rest }) => ({
        url: `/v1/projects/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: ['Project', 'Folder'],
      // A move announces itself with its own undo snackbar, so it skips the generic one.
      onQueryStarted: async (arg, api) =>
        handleApiResult(arg.showSuccess === false ? undefined : 'Api_Project_Update_Successful')(arg, api),
    }),
    deleteProject: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: handleApiResult('Api_Project_Delete_Successful'),
    }),
  }),
});

export const thumbnailCache = new Map<string, string | null>();

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useLazyGetProjectQuery,
  useLazyGetProjectFileQuery,
  useLazyGetProjectThumbnailQuery,
  useAddProjectMutation,
  useUpdateProjectMutation,
  usePatchProjectMutation,
  useDeleteProjectMutation,
} = projectApiSlice;
